/**
 * La demi-journée d'un mouvement de matériel, mise en mots.
 *
 * *Demande de Chris, 04/09/2026 : « il faut être beaucoup plus précis, tu dois indiquer à quelle
 * heure la machine est prévue d'arrêter de travailler ; si la machine est affectée sur
 * l'après-midi alors il faut proposer transport lendemain matin. »*
 *
 * Le produit ne connaît pas les heures — il compte en demi-journées — mais « le matin » et
 * « l'après-midi » répondent à la question posée : peut-on charger l'engin ce soir-là, ou
 * faut-il revenir demain.
 *
 * ## Pourquoi ce module existe à part de l'écran
 *
 * Parce que le défaut qu'il ferme était un défaut de PHRASE, pas de calcul. Un mouvement ajouté
 * à la main n'a pas de demi-journée : le responsable a posé un jour. L'écran écrivait « le
 * matin » par défaut, et cette phrase se lisait comme un choix — le premier chauffeur à la lire
 * aurait chargé l'engin le matin sans que personne l'ait décidé. Une valeur absente doit
 * s'afficher comme absente.
 */

import { formaterDateCourte } from "@planif/moteur";

/** Ce qu'on écrit quand la demi-journée est connue. */
export function direLeMoment(position: "matin" | "apres_midi"): string {
  return position === "matin" ? "le matin" : "l'après-midi";
}

/** Ce qu'on écrit quand elle ne l'est pas. Aucune confusion possible avec une moitié de jour. */
export const MOMENT_A_PRECISER = "demi-journée à préciser";

/**
 * Le moment du VOYAGE, tel que la ligne l'annonce.
 *
 * `alerte` dit à l'écran de le montrer comme un manque et non comme une information : ce n'est
 * pas une nuance de style, c'est la différence entre « on charge le matin » et « personne ne
 * sait encore quand on charge ».
 */
export function momentDuVoyage(position: "matin" | "apres_midi" | null): {
  readonly texte: string;
  readonly alerte: boolean;
} {
  if (position === null) return { texte: MOMENT_A_PRECISER, alerte: true };
  return { texte: direLeMoment(position), alerte: false };
}

/**
 * Ce que la ligne dit du travail : la borne qui EXPLIQUE la date du voyage.
 *
 * *« Travail jusqu'au mardi 8 septembre l'après-midi »* justifie un repli le mercredi matin.
 * Sans cette phrase, le responsable ne peut que croire l'application ; avec elle, il peut la
 * contredire — ce qui est le but.
 *
 * Rend `null` quand il n'y a pas de travail derrière le mouvement (un voyage ajouté à la main),
 * et l'écran n'écrit alors aucune borne plutôt qu'une borne inventée.
 */
export function borneDuTravail(mouvement: {
  readonly sens: "amene" | "repli";
  readonly dateTravail: string | null;
  readonly positionTravail: "matin" | "apres_midi" | null;
}): { readonly prefixe: string; readonly moment: string } | null {
  if (mouvement.dateTravail === null || mouvement.positionTravail === null) return null;
  return {
    prefixe: mouvement.sens === "amene" ? "travail dès " : "travail jusqu'au ",
    moment: direLeMoment(mouvement.positionTravail),
  };
}

/* ---------------------------------------------------------------------------
 * Le transfert : le créneau, et la fenêtre où il vit
 * ------------------------------------------------------------------------- */

/**
 * Un créneau de transfert, tel qu'un bouton l'annonce : `mer. 09/09 · le matin`.
 *
 * Court, parce qu'il y en a jusqu'à huit côte à côte et que le responsable les compare d'un coup
 * d'œil. Le jour de la semaine y est, et il n'est pas décoratif : *« mardi »* dit tout de suite
 * si le voyage tombe un jour où l'atelier charge, quand *« 09/09 »* demande un calcul.
 */
export function direLeCreneau(creneau: {
  readonly date: string;
  readonly position: "matin" | "apres_midi";
}): string {
  return `${formaterDateCourte(creneau.date)} · ${direLeMoment(creneau.position)}`;
}

/**
 * La fenêtre entière, en une phrase — ce que l'outil sait, avant que le responsable tranche.
 *
 * *« Tu dois me donner clairement les transferts possibles sur les trois prochains jours »*
 * (Chris, 04/09/2026). La phrase dit les deux bornes, jamais un jour retenu : l'application ne
 * choisit pas.
 *
 * Une liste vide n'est pas un blanc à masquer, c'est une réponse : les deux chantiers ne se
 * recouvrent plus.
 */
export function direLaFenetre(
  creneaux: readonly { readonly date: string; readonly position: "matin" | "apres_midi" }[],
): string {
  const premier = creneaux[0];
  const dernier = creneaux[creneaux.length - 1];
  if (premier === undefined || dernier === undefined) return "aucun créneau possible";
  if (creneaux.length === 1) return `seul créneau possible : ${direLeCreneau(premier)}`;
  return `possible du ${direLeCreneau(premier)} au ${direLeCreneau(dernier)}`;
}

/**
 * Ce que la règle disait, quand le responsable a choisi une autre moitié de jour.
 *
 * *Rien ne s'efface en silence dans cet écran* : un voyage déplacé du matin à l'après-midi doit
 * porter la trace de ce qu'il était, sinon personne ne sait au regard suivant qu'il y a eu un
 * choix. Même raison que les deux dates d'un mouvement décalé.
 */
export function direLaMoitieCorrigee(mouvement: {
  readonly position: "matin" | "apres_midi" | null;
  readonly positionDeduite: "matin" | "apres_midi" | null;
}): string | null {
  const { position, positionDeduite } = mouvement;
  if (position === null || positionDeduite === null) return null;
  if (position === positionDeduite) return null;
  return `le planning implique ${direLeMoment(positionDeduite)}`;
}

/* ---------------------------------------------------------------------------
 * Le geste : préciser la demi-journée à la main
 * ------------------------------------------------------------------------- */

/** L'état des deux boutons de demi-journée, et s'il y a une règle où revenir. */
export type EtatDeLaMoitie = {
  /** Celle que le responsable a écrite, ou `null` si la règle décide encore. */
  readonly choisie: "matin" | "apres_midi" | null;
  /** Vrai quand un choix existe ET qu'une règle existe pour le remplacer. */
  readonly peutRevenirAuPlanning: boolean;
  /** Vrai quand rien ne dit la demi-journée : ni règle, ni choix. */
  readonly aPreciser: boolean;
};

/**
 * Ce que les boutons de demi-journée doivent montrer.
 *
 * *« Mets le bouton pour préciser la demi-journée »* (Chris, 04/09/2026).
 *
 * ## Trois états, et le troisième est celui qui compte
 *
 * Un mouvement DÉDUIT a toujours une demi-journée : la règle la donne. Le bouton la corrige —
 * *« finalement on charge cet engin le matin »* — et « au planning » rend la main à la règle.
 *
 * Un mouvement AJOUTÉ à la main n'en a aucune : il n'y a pas de travail derrière lui, donc pas
 * de règle. Le bouton est alors la **seule** source, et tant qu'il n'a pas servi la ligne dit
 * « demi-journée à préciser ». Il n'y a rien où revenir, donc pas de troisième bouton.
 *
 * ## Pourquoi `choisie` et non une comparaison
 *
 * Choisir la moitié que la règle impliquait déjà est un geste légitime — c'est même la façon de
 * FIGER un voyage contre un glissement du planning. Le déduire d'une comparaison rendrait ce
 * choix-là invisible, et le clic suivant l'effacerait sans que personne l'ait voulu.
 */
export function etatDeLaMoitie(mouvement: {
  readonly moitieChoisie: "matin" | "apres_midi" | null;
  readonly positionDeduite: "matin" | "apres_midi" | null;
}): EtatDeLaMoitie {
  const { moitieChoisie, positionDeduite } = mouvement;
  return {
    choisie: moitieChoisie,
    peutRevenirAuPlanning: moitieChoisie !== null && positionDeduite !== null,
    aPreciser: moitieChoisie === null && positionDeduite === null,
  };
}
